import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, BarChart2Icon, DatabaseIcon, LayersIcon, UsersIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { MetricCard } from '../../components/MetricCard';
import { SectionHeading } from '../../components/SectionHeading';
export function AcquisitionCaseStudy() {
  return <div className="bg-cream" data-id="element-229">
      {/* Hero Section */}
      <section className="py-16 bg-white" data-id="element-230">
        <div className="container mx-auto px-6" data-id="element-231">
          <div className="max-w-4xl mx-auto" data-id="element-232">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-navy-dark mb-6" data-id="element-233">
              M&A Integration
            </h1>
            <p className="text-lg text-navy mb-8" data-id="element-234">
              Leading product integration for two major acquisitions while
              maintaining customer experience and minimizing churn.
            </p>
            <div className="mb-12" data-id="element-235">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="M&A Integration" className="w-full h-auto rounded-lg shadow-lg" data-id="element-236" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-237">
              <MetricCard value="90%" label="Customer Retention During DK Acquisition" data-id="element-238" />
              <MetricCard value="4,000+" label="Customers Migrated to Calo in 5 Months" data-id="element-239" />
            </div>
          </div>
        </div>
      </section>
      {/* Challenge Section */}
      <section className="py-16 bg-cream" data-id="element-240">
        <div className="container mx-auto px-6" data-id="element-241">
          <div className="max-w-4xl mx-auto" data-id="element-242">
            <SectionHeading title="The Challenge" subtitle="Integrating different platforms, data systems, and customer bases without losing users" data-id="element-243" />
            <div className="bg-white rounded-lg shadow-sm p-8 mb-12" data-id="element-244">
              <p className="text-navy mb-6" data-id="element-245">
                I led product integration for two major acquisitions:
              </p>
              <div className="space-y-8" data-id="element-246">
                <div data-id="element-247">
                  <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-248">
                    Acquisition 1: Detox Kitchen into Fresh Fitness Food
                  </h3>
                  <p className="text-navy mb-4" data-id="element-249">
                    Fresh Fitness Food acquired Detox Kitchen, a premium meal
                    delivery service with a different product offering, customer
                    base, and technical infrastructure. The challenge was to
                    integrate these businesses without disrupting either
                    customer experience.
                  </p>
                  <div className="bg-cream rounded-lg p-6" data-id="element-250">
                    <h4 className="text-lg font-medium text-navy-dark mb-3" data-id="element-251">
                      Key Challenges:
                    </h4>
                    <ul className="space-y-2" data-id="element-252">
                      <li className="flex" data-id="element-253">
                        <span className="text-coral-dark mr-3" data-id="element-254">•</span>
                        <span className="text-navy" data-id="element-255">
                          Different tech stacks and data structures
                        </span>
                      </li>
                      <li className="flex" data-id="element-256">
                        <span className="text-coral-dark mr-3" data-id="element-257">•</span>
                        <span className="text-navy" data-id="element-258">
                          Distinct brand identities and customer expectations
                        </span>
                      </li>
                      <li className="flex" data-id="element-259">
                        <span className="text-coral-dark mr-3" data-id="element-260">•</span>
                        <span className="text-navy" data-id="element-261">
                          Separate production facilities and operations
                        </span>
                      </li>
                      <li className="flex" data-id="element-262">
                        <span className="text-coral-dark mr-3" data-id="element-263">•</span>
                        <span className="text-navy" data-id="element-264">
                          Need to maintain premium customer experience during
                          transition
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div data-id="element-265">
                  <h3 className="text-xl font-display font-semibold text-navy-dark mb-3" data-id="element-266">
                    Acquisition 2: Fresh Fitness Food into Calo
                  </h3>
                  <p className="text-navy mb-4" data-id="element-267">
                    Later, Fresh Fitness Food itself was acquired by Calo,
                    requiring another complex integration process. This time, we
                    needed to migrate 4,000 high-value subscription customers to
                    an entirely new platform.
                  </p>
                  <div className="bg-cream rounded-lg p-6" data-id="element-268">
                    <h4 className="text-lg font-medium text-navy-dark mb-3" data-id="element-269">
                      Key Challenges:
                    </h4>
                    <ul className="space-y-2" data-id="element-270">
                      <li className="flex" data-id="element-271">
                        <span className="text-coral-dark mr-3" data-id="element-272">•</span>
                        <span className="text-navy" data-id="element-273">
                          Migrating complex subscription data and meal
                          preferences
                        </span>
                      </li>
                      <li className="flex" data-id="element-274">
                        <span className="text-coral-dark mr-3" data-id="element-275">•</span>
                        <span className="text-navy" data-id="element-276">
                          Ensuring nutrition profiles and dietary requirements
                          transferred accurately
                        </span>
                      </li>
                      <li className="flex" data-id="element-277">
                        <span className="text-coral-dark mr-3" data-id="element-278">•</span>
                        <span className="text-navy" data-id="element-279">
                          Managing payment method transitions for recurring
                          billing
                        </span>
                      </li>
                      <li className="flex" data-id="element-280">
                        <span className="text-coral-dark mr-3" data-id="element-281">•</span>
                        <span className="text-navy" data-id="element-282">
                          Maintaining service continuity during the transition
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Solution Section */}
      <section className="py-16 bg-white" data-id="element-283">
        <div className="container mx-auto px-6" data-id="element-284">
          <div className="max-w-4xl mx-auto" data-id="element-285">
            <SectionHeading title="The Solution" subtitle="Phased integration strategy aligning platforms, data migration, and customer communication" data-id="element-286" />
            <div className="space-y-12" data-id="element-287">
              <div data-id="element-288">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-289">
                  My Approach to M&A Integration
                </h3>
                <p className="text-navy mb-6" data-id="element-290">
                  For both acquisitions, I developed a comprehensive integration
                  strategy focused on preserving customer experience while
                  efficiently combining technical systems.
                </p>
              </div>
              <div data-id="element-291">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-292">
                  Detox Kitchen Integration
                </h3>
                <div className="space-y-6" data-id="element-293">
                  <div className="flex" data-id="element-294">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-295">
                      <LayersIcon size={20} data-id="element-296" />
                    </div>
                    <div data-id="element-297">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-298">
                        Platform Alignment
                      </h4>
                      <p className="text-navy" data-id="element-299">
                        I led the technical assessment of both platforms and
                        created a roadmap to gradually migrate Detox Kitchen's
                        functionality into the FFF platform. Rather than forcing
                        an immediate switch, we maintained both systems during a
                        transition period while building DK-specific features
                        into the FFF platform.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-300">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-301">
                      <DatabaseIcon size={20} data-id="element-302" />
                    </div>
                    <div data-id="element-303">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-304">
                        Data Migration & Transformation
                      </h4>
                      <p className="text-navy" data-id="element-305">
                        I designed a data migration strategy that preserved all
                        customer history, preferences, and subscription details
                        while transforming them to fit the FFF data model. This
                        included mapping different meal categories, dietary
                        preferences, and subscription models.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-306">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-307">
                      <UsersIcon size={20} data-id="element-308" />
                    </div>
                    <div data-id="element-309">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-310">
                        Customer Experience Continuity
                      </h4>
                      <p className="text-navy" data-id="element-311">
                        I developed a communication plan and transition
                        experience that maintained brand continuity for DK
                        customers while gradually introducing them to the
                        enhanced capabilities of the combined platform. We
                        preserved DK-specific recipes and meal plans while
                        adding FFF's personalization options.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-312">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-313">
                  Calo Integration
                </h3>
                <div className="space-y-6" data-id="element-314">
                  <div className="flex" data-id="element-315">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-316">
                      <BarChart2Icon size={20} data-id="element-317" />
                    </div>
                    <div data-id="element-318">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-319">
                        Segmented Migration Approach
                      </h4>
                      <p className="text-navy" data-id="element-320">
                        I developed a segment-by-segment migration plan that
                        prioritized different customer cohorts based on
                        subscription type, location, and dietary requirements.
                        This allowed us to test and refine the migration process
                        with smaller groups before scaling to the full customer
                        base.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-321">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-322">
                      <LayersIcon size={20} data-id="element-323" />
                    </div>
                    <div data-id="element-324">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-325">
                        Feature Parity Analysis & Development
                      </h4>
                      <p className="text-navy" data-id="element-326">
                        I conducted a detailed feature parity analysis between
                        the FFF and Calo platforms, identifying gaps that needed
                        to be addressed before migration. I then prioritized
                        development of missing features to ensure FFF customers
                        would have a seamless transition.
                      </p>
                    </div>
                  </div>
                  <div className="flex" data-id="element-327">
                    <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-328">
                      <UsersIcon size={20} data-id="element-329" />
                    </div>
                    <div data-id="element-330">
                      <h4 className="text-lg font-medium text-navy-dark mb-1" data-id="element-331">
                        White-Glove Transition Experience
                      </h4>
                      <p className="text-navy" data-id="element-332">
                        I designed a "white-glove" transition experience for
                        high-value customers, including personalized onboarding,
                        data verification, and priority support during
                        migration. This ensured our most valuable customers felt
                        cared for during the change.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-333">
                <h3 className="text-xl font-display font-semibold text-navy-dark mb-4" data-id="element-334">
                  How My Domain Expertise Made a Difference
                </h3>
                <p className="text-navy mb-6" data-id="element-335">
                  My background in nutrition and wellness was crucial during
                  these integrations:
                </p>
                <ul className="space-y-3" data-id="element-336">
                  <li className="flex" data-id="element-337">
                    <span className="text-coral-dark mr-3" data-id="element-338">•</span>
                    <span className="text-navy" data-id="element-339">
                      I could accurately map different nutritional approaches
                      between platforms, ensuring dietary requirements were
                      preserved
                    </span>
                  </li>
                  <li className="flex" data-id="element-340">
                    <span className="text-coral-dark mr-3" data-id="element-341">•</span>
                    <span className="text-navy" data-id="element-342">
                      I understood the health and wellness customer mindset,
                      allowing me to craft messaging that addressed their
                      concerns
                    </span>
                  </li>
                  <li className="flex" data-id="element-343">
                    <span className="text-coral-dark mr-3" data-id="element-344">•</span>
                    <span className="text-navy" data-id="element-345">
                      I could identify which product features were truly
                      important to retain versus which could be safely
                      deprecated
                    </span>
                  </li>
                  <li className="flex" data-id="element-346">
                    <span className="text-coral-dark mr-3" data-id="element-347">•</span>
                    <span className="text-navy" data-id="element-348">
                      I ensured that the technical integration preserved the
                      nutritional integrity of each brand's offering
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Results Section */}
      <section className="py-16 bg-cream" data-id="element-349">
        <div className="container mx-auto px-6" data-id="element-350">
          <div className="max-w-4xl mx-auto" data-id="element-351">
            <SectionHeading title="The Outcome" subtitle="Successful integrations with minimal customer disruption" data-id="element-352" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-353">
              <MetricCard value="90%" label="Customer Retention During DK Acquisition" className="bg-sage-light" data-id="element-354" />
              <MetricCard value="4,000+" label="Customers Successfully Migrated to Calo" className="bg-sage-light" data-id="element-355" />
            </div>
            <div className="bg-white rounded-lg shadow-sm p-8" data-id="element-356">
              <div className="space-y-6" data-id="element-357">
                <div data-id="element-358">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-359">
                    Detox Kitchen Integration Results
                  </h3>
                  <ul className="space-y-2" data-id="element-360">
                    <li className="flex" data-id="element-361">
                      <span className="text-coral-dark mr-3" data-id="element-362">•</span>
                      <span className="text-navy" data-id="element-363">
                        Maintained 90% customer retention throughout the 5-month
                        integration process
                      </span>
                    </li>
                    <li className="flex" data-id="element-364">
                      <span className="text-coral-dark mr-3" data-id="element-365">•</span>
                      <span className="text-navy" data-id="element-366">
                        Successfully merged production operations, increasing
                        kitchen efficiency by 22%
                      </span>
                    </li>
                    <li className="flex" data-id="element-367">
                      <span className="text-coral-dark mr-3" data-id="element-368">•</span>
                      <span className="text-navy" data-id="element-369">
                        Created a unified customer data platform while
                        preserving brand-specific experiences
                      </span>
                    </li>
                    <li className="flex" data-id="element-370">
                      <span className="text-coral-dark mr-3" data-id="element-371">•</span>
                      <span className="text-navy" data-id="element-372">
                        Enabled cross-selling between brands, increasing average
                        order value by 18%
                      </span>
                    </li>
                    <li className="flex" data-id="element-373">
                      <span className="text-coral-dark mr-3" data-id="element-374">•</span>
                      <span className="text-navy" data-id="element-375">
                        Expanded accessibility of premium nutrition services to
                        more customers
                      </span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-376">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-377">
                    Calo Integration Results
                  </h3>
                  <ul className="space-y-2" data-id="element-378">
                    <li className="flex" data-id="element-379">
                      <span className="text-coral-dark mr-3" data-id="element-380">•</span>
                      <span className="text-navy" data-id="element-381">
                        Successfully migrated all 4,000+ customers within 5
                        months with minimal churn
                      </span>
                    </li>
                    <li className="flex" data-id="element-382">
                      <span className="text-coral-dark mr-3" data-id="element-383">•</span>
                      <span className="text-navy" data-id="element-384">
                        Preserved all customer dietary preferences and
                        subscription settings with 99.8% accuracy
                      </span>
                    </li>
                    <li className="flex" data-id="element-385">
                      <span className="text-coral-dark mr-3" data-id="element-386">•</span>
                      <span className="text-navy" data-id="element-387">
                        Maintained service continuity with zero missed
                        deliveries during transition
                      </span>
                    </li>
                    <li className="flex" data-id="element-388">
                      <span className="text-coral-dark mr-3" data-id="element-389">•</span>
                      <span className="text-navy" data-id="element-390">
                        Leveraged the integration to introduce enhanced
                        personalization features
                      </span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-391">
                  <h3 className="text-lg font-display font-semibold text-navy-dark mb-2" data-id="element-392">
                    Business Impact
                  </h3>
                  <p className="text-navy" data-id="element-393">
                    These successful integrations demonstrated my ability to
                    manage complex product transitions while maintaining
                    customer satisfaction. The skills developed during these
                    acquisitions have given me valuable expertise in:
                  </p>
                  <ul className="space-y-2 mt-3" data-id="element-394">
                    <li className="flex" data-id="element-395">
                      <span className="text-coral-dark mr-3" data-id="element-396">•</span>
                      <span className="text-navy" data-id="element-397">
                        Leading technical due diligence for acquisitions
                      </span>
                    </li>
                    <li className="flex" data-id="element-398">
                      <span className="text-coral-dark mr-3" data-id="element-399">•</span>
                      <span className="text-navy" data-id="element-400">
                        Developing phased migration strategies for complex
                        platforms
                      </span>
                    </li>
                    <li className="flex" data-id="element-401">
                      <span className="text-coral-dark mr-3" data-id="element-402">•</span>
                      <span className="text-navy" data-id="element-403">
                        Managing change for both customers and internal teams
                      </span>
                    </li>
                    <li className="flex" data-id="element-404">
                      <span className="text-coral-dark mr-3" data-id="element-405">•</span>
                      <span className="text-navy" data-id="element-406">
                        Identifying opportunities to enhance product offerings
                        during integration
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Navigation Section */}
      <section className="py-12 bg-white" data-id="element-407">
        <div className="container mx-auto px-6" data-id="element-408">
          <div className="max-w-4xl mx-auto flex justify-between" data-id="element-409">
            <Button href="/case-study/platform" variant="outline" data-id="element-410">
              <ArrowLeftIcon size={16} className="mr-2" data-id="element-411" />
              Previous Case Study
            </Button>
            <Button href="/case-study/app" data-id="element-412">
              Next Case Study
              <ArrowRightIcon size={16} className="ml-2" data-id="element-413" />
            </Button>
          </div>
        </div>
      </section>
    </div>;
}